import { createSlice } from "@reduxjs/toolkit";
import { createAppSelector } from "../store/utils.js";
import { Accommodation, AccommodationState, Language } from "../types/index.js";
import { fetch } from "../utils/utils.js";
import { showAlert } from "./alerts.js";
import { createAppAsyncThunk } from "./utils.js";

const sliceName = "accommodation";

const initialState: AccommodationState = {};

export const getAccommodation = createAppAsyncThunk(
  `${sliceName}/fetchAccommodation`,
  async (
    { siteId, languageId }: { siteId: string; languageId: Language },
    { dispatch },
  ) => {
    try {
      const accommodation = await fetch({ dispatch, languageId })
        .get(`sites/${siteId}/accommodation`)
        .json<Accommodation>();
      return accommodation;
    } catch (error) {
      dispatch(
        showAlert("Beim Laden der Betriebsdaten ist ein Fehler aufgetreten!"),
      );
      throw error;
    }
  },
  {
    condition: ({ languageId }, { getState }) => {
      const loadState = getState().loadStates.accommodation[languageId];
      return loadState === "unloaded" || loadState === undefined;
    },
  },
);

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAccommodation.fulfilled, (state, action) => {
      const { languageId } = action.meta.arg;
      state[languageId] = action.payload;
    });
  },
});

// the accommodation features regarding
// “Overview of operating features/inclusive services”
const featureIdsToOmit = [
  11, 154, 155, 156, 157, 158, 159, 160, 161, 162, 163, 164, 165, 166,
];

export const getAccommodationFeatures = (
  accommodation: AccommodationState,
  languageId: Language,
) => {
  const features =
    accommodation[languageId]?.features.filter(
      ({ id }) => !featureIdsToOmit.includes(id),
    ) ?? [];

  return {
    ids: features.map(({ id }) => id),
    titles: features.reduce<{ [id: number]: string }>(
      (accumulator, { id, title }) => {
        accumulator[id] = title;
        return accumulator;
      },
      {},
    ),
  };
};

export const selectAccommodationFeatures = createAppSelector(
  [(state) => state.accommodation, (_, languageId: Language) => languageId],
  (accommodation, languageId) =>
    getAccommodationFeatures(accommodation, languageId),
);

export default slice.reducer;
